import { createSlice } from "@reduxjs/toolkit";
import {
  readCustomerAuthStorage,
  writeCustomerAuthStorage,
  clearCustomerAuthStorage,
} from "../utils/customerAuthStorage";

function persistCustomerAuth(restaurantSlug, { customerToken, customerRefreshToken, customerUser }) {
  writeCustomerAuthStorage(restaurantSlug, {
    customerToken,
    customerRefreshToken,
    customerUser,
  });
}

function loadPersistedCustomer(restaurantSlug) {
  if (!restaurantSlug) return null;
  return readCustomerAuthStorage(restaurantSlug);
}

const initialState = {
  customerUser: null,
  customerToken: null,
  customerRefreshToken: null,
  isCustomerLoggedIn: false,
  currentRestaurantSlug: null,
  isHydrated: false,
};

const customerAuthSlice = createSlice({
  name: "customerAuth",
  initialState,
  reducers: {
    customerLogin: (state, action) => {
      const { token, refreshToken, customer, restaurantSlug } = action.payload;
      state.customerToken = token;
      state.customerRefreshToken = refreshToken || null;
      state.customerUser = customer;
      state.isCustomerLoggedIn = true;
      state.currentRestaurantSlug = restaurantSlug || state.currentRestaurantSlug || null;
      state.isHydrated = true;
      persistCustomerAuth(state.currentRestaurantSlug, {
        customerToken: token,
        customerRefreshToken: state.customerRefreshToken,
        customerUser: customer,
      });
    },
    updateCustomerToken: (state, action) => {
      const { token, refreshToken, customer, restaurantSlug } = action.payload || {};
      if (token) state.customerToken = token;
      if (refreshToken) state.customerRefreshToken = refreshToken;
      if (customer) state.customerUser = customer;
      if (restaurantSlug) state.currentRestaurantSlug = restaurantSlug;
      state.isCustomerLoggedIn = !!(state.customerToken || state.customerRefreshToken);
      state.isHydrated = true;
      persistCustomerAuth(state.currentRestaurantSlug, {
        customerToken: state.customerToken,
        customerRefreshToken: state.customerRefreshToken,
        customerUser: state.customerUser,
      });
    },
    customerLogout: (state, action) => {
      const targetRestaurantSlug = action.payload?.restaurantSlug || state.currentRestaurantSlug;
      state.customerToken = null;
      state.customerRefreshToken = null;
      state.customerUser = null;
      state.isCustomerLoggedIn = false;
      state.isHydrated = true;
      clearCustomerAuthStorage(targetRestaurantSlug);
    },
    restoreCustomerFromStorage: (state, action) => {
      const restaurantSlug = action.payload?.restaurantSlug;
      state.currentRestaurantSlug = restaurantSlug || null;

      if (!restaurantSlug) {
        state.customerToken = null;
        state.customerRefreshToken = null;
        state.customerUser = null;
        state.isCustomerLoggedIn = false;
        state.isHydrated = true;
        return;
      }

      const data = loadPersistedCustomer(restaurantSlug);
      if (data) {
        state.customerToken = data.customerToken ?? data.token ?? null;
        state.customerRefreshToken = data.customerRefreshToken ?? data.refreshToken ?? null;
        state.customerUser = data.customerUser ?? null;
        state.isCustomerLoggedIn = !!(state.customerToken || state.customerRefreshToken);
      } else {
        state.customerToken = null;
        state.customerRefreshToken = null;
        state.customerUser = null;
        state.isCustomerLoggedIn = false;
      }
      state.isHydrated = true;
    },
  },
});

export const {
  customerLogin,
  customerLogout,
  restoreCustomerFromStorage,
  updateCustomerToken,
} = customerAuthSlice.actions;

export const selectCustomerAuth = (state) => state.customerAuth;
export const selectIsCustomerLoggedIn = (state) =>
  state.customerAuth?.isCustomerLoggedIn ?? false;
export const selectCustomerUser = (state) => state.customerAuth?.customerUser ?? null;
export const selectCustomerToken = (state) => state.customerAuth?.customerToken ?? null;
export const selectCustomerRefreshToken = (state) =>
  state.customerAuth?.customerRefreshToken ?? null;
export const selectCustomerAuthHydrated = (state) =>
  state.customerAuth?.isHydrated ?? false;
export const selectCurrentCustomerRestaurantSlug = (state) =>
  state.customerAuth?.currentRestaurantSlug ?? null;

export default customerAuthSlice.reducer;
